import { Injectable, inject } from '@angular/core';

import { IOffer, IOfferGroupsFromServer, IOfferReport, IParams } from '@frontend/oxygen/core-utils';
import { ISiteCoreTeaserFromServer } from '@frontend/oxygen/shared-utils';
import { WINDOW } from '@frontend/vanilla/core';
import { Observable, from, of } from 'rxjs';
import { map, timeout } from 'rxjs/operators';

import { EnvironmentConfig } from '../../../client-config';
import { DEFAULT_FLAG_VARIATIONS, FEATURE_FLAGS, VARIATIONS } from '../../../lazy-modules/flagSource/constants/flag-source.constants';
import { FlagUtils } from '../../../lazy-modules/flagSource/models/flag.utils';
import { VanillaApiService } from '../../../vanillaInit/services/vanillaApi/vanilla-api.service';
import { ISSResponse } from '../../models/ss-response.model';
import { SiteServerRequestHelperService } from '../siteServerRequestHelper/site-server-request-helper.service';
import { StorageService } from '../storage/storage.service';
import { WindowRefService } from '../windowRef/window-ref.service';
import { IBannerResponseData } from './banner.service.model';
import { BANNERS, BANNER_SERVICE_ERROR_MESSAGE, OFFER_KEYS, SITECORE_API_CALL } from './enums/banners.service.enums';
import { Utils } from './utils';

@Injectable({ providedIn: 'root' })
export class BannersService {
    updatedOffers: IOffer[];
    priceNum: number;
    priceDen: number;
    private _settings: IParams;

    readonly #window = inject(WINDOW);
    private env = inject(EnvironmentConfig);
    readonly bannersConfig = this.env.configuration.BANNERS_CONFIG ?? {
        SRCSET_MOBILE: ['120', '240', '360', '480', '720', '1024'],
        SRCSET_DESKTOP: ['120', '240', '360', '480', '720', '1024'],
        SIZES_DESKTOP_BACKGROUND: '25vw',
        SIZES_DESKTOP_FOREGROUND: '15vw',
        SIZES_MOBILE_BACKGROUND: '100vw',
        SIZES_MOBILE_FOREGROUND: '40vw',
    };

    constructor(
        private vanillaApiService: VanillaApiService,
        private siteServerRequestHelperService: SiteServerRequestHelperService,
        private storageService: StorageService,
    ) {}

    clearStatImg() {
        // eslint-disable-next-line no-restricted-globals
        const statImg: HTMLElement | null = document.querySelector('#statImg');
        if (statImg) {
            statImg.style.display = 'none';
        }
    }

    /**
     * Gets offers to display in carousel - according to settings
     * @param optionsPar - IParams
     * @returns {Observable<IOfferReport>}
     */
    public fetchOffersFromAEM(optionsPar: IParams): Observable<IOfferReport> {
        this.clearStatImg();
        this._settings = optionsPar;
        if (this.#window.teaserPreload) {
            if (optionsPar.page && this.#window.teaserPreload[optionsPar.page]) {
                const response = of({
                    offers: this.groupAndMergeAndFormatOffers({
                        data: [...this.#window.teaserPreload[optionsPar.page]],
                        resolved: true,
                        message: '',
                    }),
                });
                this.#window.teaserPreload = null;
                return response;
            } else {
                this.#window.teaserPreload = null;
            }
        }
        const teasersOrgData: IBannerResponseData = this.storageService.get('teasersOrgData');
        if (this.#window.teaserPreloadLazy) {
            if (this._settings.page && this.#window.teaserPreloadLazy[this._settings.page]) {
                const response = from(this.#window.teaserPreloadLazy[this._settings.page]).pipe(
                    map((offers) => ({ providers: '', offers: offers as IOfferReport['offers'] })),
                );
                this.#window.teaserPreloadLazy = null;
                return response;
            }
            this.#window.teaserPreloadLazy = null;
        }

        if (teasersOrgData && this._settings.page === teasersOrgData.message) {
            const offers: IOffer[] = this.groupAndMergeAndFormatOffers(teasersOrgData);
            this.storageService.remove('teasersOrgData');
            return of({ providers: '', offers: offers });
        } else {
            const libraryRequest: Observable<IBannerResponseData> = this.getLibraryOffers(this._settings.page ?? '');

            // Wait for responses
            return libraryRequest.pipe(
                map((libraryInspection: any) => {
                    if (libraryInspection.resolved) {
                        const offers: IOffer[] = this.groupAndMergeAndFormatOffers(libraryInspection);
                        return { offers };
                    } else {
                        throw new Error(BANNER_SERVICE_ERROR_MESSAGE.PF);
                    }
                }),
            );
        }
    }

    /**
     *
     * @param targetInspection - object that represents results of request to Adobe Target server
     * @param libraryInspection - object that represents results of request to AEM server
     */
    public groupAndMergeAndFormatOffers(libraryInspection: IBannerResponseData): IOffer[] {
        const offerGroups: IOfferGroupsFromServer = { target: [], library: [] };

        if (libraryInspection.resolved) {
            const _libraryInspection_value = libraryInspection.data ? libraryInspection.data : [];
            offerGroups.library = _libraryInspection_value;
        }

        const formattedLibOffer = Utils.formatSitecore(offerGroups);
        this.setPersonalisedForGroups(formattedLibOffer as any);
        return this.mergeOffers(formattedLibOffer);
    }

    public setPersonalisedForGroups({ library = [], pinned = {}, rg = null }): void {
        library.forEach((offer) => this.setPersonalised(offer, false));
        this.setPersonalised(pinned, false);
        if (rg) {
            this.setPersonalised(rg, false);
        }
    }

    /**
     * Merge Offers by functional order priority, target, default, regulatory and trim based on CMS Config
     * @param offerGroups - IOfferGroupsFromServer
     * @returns - IOffer[] - Array
     */
    public mergeOffers(offerGroups: IOfferGroupsFromServer): IOffer[] {
        // separate by parts
        const target = offerGroups.target === undefined ? [] : offerGroups.target;
        const library = offerGroups.library === undefined ? [] : offerGroups.library;
        const pinned = offerGroups.pinned === undefined ? [] : offerGroups.pinned;
        const rg = offerGroups.rg;
        // deduplicate
        const _dedupe = Utils.dedupe([pinned, target, library]);
        const [ddStartList, ddTarget, ddLibrary] = _dedupe;
        // middle of carousel => plain + target
        const baseList = ddTarget.concat(ddLibrary);
        const atLeastOneNonRgPresent = Object.keys(baseList).length && Object.keys(ddStartList).length;
        const showRg = atLeastOneNonRgPresent && this._settings.maxOffers !== 1 && Object.keys(ddStartList).length;
        const positionedItemsCount = ddStartList.length + (showRg ? 1 : 0);
        const upperLimit = (this._settings.maxOffers ?? 0) - positionedItemsCount > 0 ? (this._settings.maxOffers ?? 0) - positionedItemsCount : 0;
        // trim plain + target
        const trimmedBaseList = baseList.slice(0, upperLimit);

        // construct carousel banners list => plain + target + pinned
        let orderedList: any = [].concat(ddStartList, trimmedBaseList);
        if (showRg && rg && rg.length) {
            orderedList = orderedList.concat(rg[0]);
        }

        // reassure that exact number of banners that comes from settings is shown
        if ((this._settings.maxOffers ?? 0) < orderedList.length) {
            orderedList = orderedList.slice(0, this._settings.maxOffers);
            if (showRg && orderedList.length > 1 && rg && rg.length) {
                orderedList[orderedList.length - 1] = rg[0];
            }
        }

        //   return finalOffers
        const formattedTrimmedList = orderedList.map((offer: ISiteCoreTeaserFromServer, index: number) => this.formatOfferResponse(offer, index));
        return formattedTrimmedList;
    }

    /**
     * Get Live Odds from Siteserve
     * @param offer - object that represents Offers
     * @returns - IOffer Object
     */
    public getOdds(offer: IOffer): IOffer {
        const status = 'A';
        if (offer.outcomeId) {
            this.getEvents(offer.outcomeId).then((response: { SSResponse: ISSResponse }) => {
                if (response.SSResponse.children[0].event) {
                    const oddsValue = response.SSResponse.children[0].event.children[0].market.children[0].outcome.children[0].price;
                    const eventStatus: string = response.SSResponse.children[0].event.eventStatusCode;
                    const marketStatus: string = response.SSResponse.children[0].event.children[0].market.marketStatusCode;
                    const outcomeStatus: string = response.SSResponse.children[0].event.children[0].market.children[0].outcome.outcomeStatusCode;
                    if (eventStatus === status && marketStatus === status && outcomeStatus === status) {
                        offer.currentOdds = `${oddsValue.priceNum}/${oddsValue.priceDen}`;
                        offer.bannerStatus = true;
                    } else {
                        offer.bannerStatus = false;
                    }
                } else {
                    offer.bannerStatus = false;
                }
            });
        }
        return offer;
    }

    /**
     * Get format Siteserve response
     * @param offer - object that represents Offers
     * @returns - IOffer Object
     */
    public formatOfferResponse(offer: ISiteCoreTeaserFromServer, index: number) {
        const offerObj: IOffer = {};
        offerObj.index = index;
        offerObj.Id = this.getObjectKeyValue(offer, OFFER_KEYS.itemId);
        offerObj.title = this.getObjectKeyValue(offer, OFFER_KEYS.title);
        offerObj.introductorytext = this.getObjectKeyValue(offer, OFFER_KEYS.introductoryText);
        offerObj.subtitle = this.getObjectKeyValue(offer, OFFER_KEYS.subTitle);
        offerObj.itemName = this.getObjectKeyValue(offer, OFFER_KEYS.itemName);
        offerObj.bannerStatus = true;
        offerObj.tcText = this.getObjectKeyValue(offer, OFFER_KEYS.keyTermsAndConditions);
        offerObj.brand = this._settings.brand;
        offerObj.tcLink = offer.termsAndConditionsLink ? offer.termsAndConditionsLink.url : offer.bannerLink ? offer.bannerLink.url : '';
        offerObj.tcTarget =
            offer.termsAndConditionsLink && offer.termsAndConditionsLink.attributes ? offer.termsAndConditionsLink.attributes.target : '';
        offerObj.imgUrl = offer.backgroundImage ? this.getObjectKeyValue(offer.backgroundImage, OFFER_KEYS.src) : '';
        offerObj.altText = offer.backgroundImage ? this.getObjectKeyValue(offer.backgroundImage, OFFER_KEYS.alt) : '';
        offerObj.link = (offer && offer.bannerLink && offer.bannerLink.url) || '';
        offerObj.textAlignment = offer && offer.textAlignment;
        if (offer) {
            if (offer.bannerLink && offer.bannerLink.attributes) {
                offerObj.target = this.getObjectKeyValue(offer.bannerLink.attributes, OFFER_KEYS.target);
            }
            if (offer.foregroundImage) {
                offerObj.foregroundimage = this.getObjectKeyValue(offer.foregroundImage, OFFER_KEYS.src);
                offerObj.foregroundAltText = this.getObjectKeyValue(offer.foregroundImage, OFFER_KEYS.alt);
            }
            if (offer.liveOddsBannerSelectionID) {
                offerObj.bannerStatus = false;
                offerObj.outcomeId = offer.liveOddsBannerSelectionID;
                offerObj.previousOdds = this.getObjectKeyValue(offer, OFFER_KEYS.previousOddsValue);
            }
            if ((offer.htmlid || offer.htmlClass) && offer.customCSS) {
                offerObj.htmlid = offer.htmlid;
                offerObj.htmlClass = offer.htmlClass;
                offerObj.customCSS = offer.customCSS;
            }
            offerObj.CTA = (offer && offer.CTA && offer.CTA.url) || '';
        }
        return offerObj;
    }

    /**
     * Verify if Obj is undefined and
     * get values from object else return empty
     * @param obj - object
     * @returns - String
     */
    public getObjectKeyValue(obj: Record<string, any> | undefined, key: string): string {
        return (obj && obj[key]) || '';
    }

    /**
     * Get Offers from Sitecore using Vanilla api
     * @param offer - object that represents Offers
     * @returns - String
     */
    public getLibraryOffers(page: string): Observable<IBannerResponseData> {
        const subPath = ['priority', 'regulatory', 'default'];
        const response: IBannerResponseData = { data: null, resolved: true, message: null };
        const requestParams = {
            path: `${SITECORE_API_CALL.FOLDER}/${page}`,
            subPaths: subPath,
            prefetchDepth: `${SITECORE_API_CALL.PREFETCHDEPTH}`,
        };
        const apioptions = {
            prefix: `${SITECORE_API_CALL.PREFIX}`,
        };
        return this.vanillaApiService.post(`${SITECORE_API_CALL.PATH}`, requestParams, apioptions).pipe(
            timeout(SITECORE_API_CALL.TIMEOUT),
            map((value) => {
                response.data = value;
                return response;
            }),
        );
    }

    /**
     * Get Odds from SS
     * @param ids - Selection ID
     * @returns - Promise
     */
    public getEvents(ids: string | string[]): Promise<{ SSResponse: ISSResponse }> {
        return this.siteServerRequestHelperService.getEventsByOutcomes({
            outcomesIds: ids,
        });
    }

    /**
     * Set Personalized for target offers
     * @param offer - object that represents Offers
     * @param personalised - boolean status
     * @returns - String
     */
    private setPersonalised(offer: IOffer, personalised: boolean): void {
        if (offer) {
            offer.personalised = personalised;
        }
    }

    /**
     * To get the latest LD flags for banners
     * @param wr - window object
     * @param pageName - String
     * @returns - boolean
     */
    getLatestVariation(wr: WindowRefService, pageName: string): boolean {
        let sportsbookBannersLDVar: string = DEFAULT_FLAG_VARIATIONS.SportsbookBanners as string;
        let showBannersLD: boolean;

        if (wr?.nativeWindow?.ldClient) {
            sportsbookBannersLDVar = FlagUtils.getFlagVariation(FEATURE_FLAGS.SportsbookBanners, DEFAULT_FLAG_VARIATIONS.SportsbookBanners);

            if (
                ((pageName === BANNERS.homePageName || pageName === BANNERS.mobileHomePageName) &&
                    sportsbookBannersLDVar === VARIATIONS.NO_HOME_PAGE_BANNERS) ||
                sportsbookBannersLDVar === VARIATIONS.NO_BANNERS_ALL
            ) {
                showBannersLD = false;
            } else {
                showBannersLD = true;
            }
            return showBannersLD;
        }
        return false;
    }
}
